import { CameraOutlined, DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Modal, Row, Select, Switch, Upload } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { BRAND } from '@/constants/brand';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import {
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useUploadBannerImageMutation,
} from '@/modules/banners/bannersApi';
import { useLazyGetBrandsQuery } from '@/modules/brands/brandsApi';
import { useLazyGetCategoriesQuery } from '@/modules/categories/categoriesApi';
import { useLazyGetProductsQuery } from '@/modules/products/productsApi';
import type { Banner, CreateBannerPayload, UpdateBannerPayload } from '@/types/banner';
import { resolveMutationResult } from '@/utils/apiResponse';
import { getBannerImageUrl, extractBannerImageFilename } from '@/utils/media';
import { ucfirst } from '@/utils/string';
import styles from './BannerFormModal.module.scss';

interface BannerFormModalProps {
  open: boolean;
  banner?: Banner | null;
  onClose: () => void;
}

interface BannerFormValues {
  categoryId?: string;
  brandId?: string;
  productId?: string;
  link: string;
  isActive: boolean;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const DROPDOWN_LIST_PARAMS = {
  page: 1,
  limit: 100,
  sortBy: 'name',
  sortOrder: 'asc' as const,
  status: 'active' as const,
};

function buildSelectOptions(
  items: Array<{ id: string; name: string }>,
  selectedId?: string | null,
  selectedName?: string | null,
) {
  const options = items.map((item) => ({ label: ucfirst(item.name), value: item.id }));

  if (selectedId && selectedName && !options.some((option) => option.value === selectedId)) {
    options.unshift({ label: ucfirst(selectedName), value: selectedId });
  }

  return options;
}

function buildCreateBannerPayload(image: string, values: BannerFormValues): CreateBannerPayload {
  const payload: CreateBannerPayload = {
    image: extractBannerImageFilename(image),
    isActive: values.isActive,
  };

  if (values.categoryId) {
    payload.categoryId = values.categoryId;
    return payload;
  }

  if (values.brandId) {
    payload.brandId = values.brandId;
    return payload;
  }

  if (values.productId) {
    payload.productId = values.productId;
    return payload;
  }

  const link = values.link.trim();
  if (link) {
    payload.link = link;
  }

  return payload;
}

function buildUpdateBannerPayload(
  image: string | undefined,
  values: BannerFormValues,
): UpdateBannerPayload {
  const payload: UpdateBannerPayload = {
    isActive: values.isActive,
  };

  if (image) {
    payload.image = extractBannerImageFilename(image);
  }

  if (values.categoryId) {
    return {
      ...payload,
      categoryId: values.categoryId,
      brandId: null,
      productId: null,
    };
  }

  if (values.brandId) {
    return {
      ...payload,
      brandId: values.brandId,
      categoryId: null,
      productId: null,
    };
  }

  if (values.productId) {
    return {
      ...payload,
      productId: values.productId,
      categoryId: null,
      brandId: null,
    };
  }

  const link = values.link.trim();

  return {
    ...payload,
    categoryId: null,
    brandId: null,
    productId: null,
    link,
  };
}

export function BannerFormModal({ open, banner, onClose }: BannerFormModalProps) {
  const notify = useNotify();
  const isEdit = Boolean(banner);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [createBanner, createState] = useCreateBannerMutation();
  const [updateBanner, updateState] = useUpdateBannerMutation();
  const [uploadBannerImage, uploadState] = useUploadBannerImageMutation();
  const [fetchCategories, { data: categoriesResult, isFetching: categoriesLoading }] =
    useLazyGetCategoriesQuery();
  const [fetchBrands, { data: brandsResult, isFetching: brandsLoading }] = useLazyGetBrandsQuery();
  const [fetchProducts, { data: productsResult, isFetching: productsLoading }] =
    useLazyGetProductsQuery();
  const isLoading = createState.isLoading || updateState.isLoading || uploadState.isLoading;
  useGlobalLoader(isLoading);

  const { control, handleSubmit, reset, watch, setValue } = useForm<BannerFormValues>({
    defaultValues: {
      categoryId: undefined,
      brandId: undefined,
      productId: undefined,
      link: '',
      isActive: true,
    },
  });

  const categoryId = watch('categoryId');
  const brandId = watch('brandId');
  const productId = watch('productId');
  const hasMapping = Boolean(categoryId || brandId || productId);

  useMutationReset(createState.reset);
  useMutationReset(updateState.reset);
  useMutationReset(uploadState.reset);

  const existingImageUrl = useMemo(
    () => getBannerImageUrl(banner?.image),
    [banner?.image],
  );

  const categoryOptions = useMemo(
    () =>
      buildSelectOptions(
        categoriesResult?.results ?? [],
        banner?.categoryId,
        banner?.categoryName,
      ),
    [banner?.categoryId, banner?.categoryName, categoriesResult?.results],
  );

  const brandOptions = useMemo(
    () => buildSelectOptions(brandsResult?.results ?? [], banner?.brandId, banner?.brandName),
    [banner?.brandId, banner?.brandName, brandsResult?.results],
  );

  const productOptions = useMemo(
    () =>
      buildSelectOptions(productsResult?.results ?? [], banner?.productId, banner?.productName),
    [banner?.productId, banner?.productName, productsResult?.results],
  );

  useEffect(() => {
    if (!open) return;

    reset({
      categoryId: banner?.categoryId ?? undefined,
      brandId: banner?.brandId ?? undefined,
      productId: banner?.productId ?? undefined,
      link: banner?.categoryId || banner?.brandId || banner?.productId ? '' : (banner?.link ?? ''),
      isActive: banner?.isActive ?? true,
    });
    setImageFile(null);
    setPreviewUrl(existingImageUrl);

    if (banner?.categoryId) {
      void fetchCategories(DROPDOWN_LIST_PARAMS);
    }
    if (banner?.brandId) {
      void fetchBrands(DROPDOWN_LIST_PARAMS);
    }
    if (banner?.productId) {
      void fetchProducts(DROPDOWN_LIST_PARAMS);
    }
  }, [
    banner,
    existingImageUrl,
    fetchBrands,
    fetchCategories,
    fetchProducts,
    open,
    reset,
  ]);

  const handleImageChange = (info: UploadChangeParam) => {
    const file = info.file.originFileObj ?? info.file;
    if (!file || !(file instanceof File)) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      notify.error('Please upload a PNG, JPG, WEBP, or GIF image.');
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      notify.error('Image must be 5MB or smaller.');
      return;
    }

    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setPreviewUrl(undefined);
  };

  const uploadImageIfNeeded = async (): Promise<string | undefined> => {
    if (!imageFile) {
      return banner?.image;
    }

    const formData = new FormData();
    formData.append('image', imageFile);
    const result = await resolveMutationResult<{ image: string }>(uploadBannerImage(formData));

    if (!result.ok) {
      notify.error(result.message);
      return undefined;
    }

    return result.data.image;
  };

  const onSubmit = async (values: BannerFormValues) => {
    const image = await uploadImageIfNeeded();
    if (!image) {
      notify.error('Banner image is required.');
      return;
    }

    const mappingCount = [values.categoryId, values.brandId, values.productId].filter(Boolean).length;
    if (mappingCount > 1) {
      notify.error('Select only one of category, brand, or product.');
      return;
    }

    const payload = buildCreateBannerPayload(image, values);

    if (isEdit) {
      if (!banner?.id) return;

      const result = await resolveMutationResult(
        updateBanner({ id: banner.id, body: buildUpdateBannerPayload(image, values) }),
      );
      if (result.ok) {
        notify.success(result.message ?? 'Banner updated successfully.');
        onClose();
        return;
      }

      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult(createBanner(payload));

    if (result.ok) {
      notify.success(result.message ?? 'Banner created successfully.');
      onClose();
      return;
    }

    notify.error(result.message);
  };

  return (
    <Modal
      title={isEdit ? 'Edit banner' : 'Add banner'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={800}
    >
      <Form layout="vertical" className={styles.form} onFinish={handleSubmit(onSubmit)}>
        <Row gutter={24} className={styles.formGrid}>
          <Col span={12} className={styles.imageColumn}>
            <Form.Item label="Image" required={!isEdit} className={styles.imageField}>
              <div className={styles.imagePicker}>
                <Upload
                  accept={ACCEPTED_TYPES.join(',')}
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={handleImageChange}
                  disabled={isLoading}
                  className={styles.imageUpload}
                >
                  <button type="button" className={styles.imageThumb}>
                {previewUrl ? (
                  <PreviewImage
                    src={previewUrl}
                    alt="Banner preview"
                    fill
                    className={styles.imagePreview}
                    stopPropagation
                  />
                ) : (
                      <span className={styles.imagePlaceholder}>
                        <PictureOutlined />
                      </span>
                    )}
                    <span className={styles.imageOverlay}>
                      <CameraOutlined />
                    </span>
                  </button>
                </Upload>

                <div className={styles.imageMeta}>
                  <p className={styles.imageTitle}>
                    {previewUrl ? 'Banner image selected' : 'Upload banner image'}
                  </p>
                  <p className={styles.imageHint}>PNG, JPG, WEBP or GIF · Max 5MB · Saved as WebP</p>
                  {previewUrl ? (
                    <div className={styles.imageActions}>
                      <Upload
                        accept={ACCEPTED_TYPES.join(',')}
                        showUploadList={false}
                        beforeUpload={() => false}
                        onChange={handleImageChange}
                        disabled={isLoading}
                      >
                        <Button size="small" type="default">
                          Change
                        </Button>
                      </Upload>
                      <Button
                        size="small"
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={handleRemoveImage}
                        disabled={isLoading}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </Form.Item>
          </Col>

          <Col span={12} className={styles.fieldsColumn}>
            <div className={styles.mappingSection}>
              <p className={styles.mappingTitle}>Link to (optional)</p>
              <p className={styles.mappingHint}>
                Choose one mapping, or leave all empty and use a custom link.
              </p>

              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Category" className={styles.mappingField}>
                    <Select
                      {...field}
                      allowClear
                      showSearch
                      placeholder="Select category"
                      options={categoryOptions}
                      loading={categoriesLoading}
                      optionFilterProp="label"
                      onOpenChange={(visible) => {
                        if (visible) void fetchCategories(DROPDOWN_LIST_PARAMS);
                      }}
                      onChange={(value) => {
                        field.onChange(value);
                        if (value) {
                          setValue('brandId', undefined);
                          setValue('productId', undefined);
                          setValue('link', '');
                        }
                      }}
                      disabled={isLoading || Boolean(brandId || productId)}
                    />
                  </Form.Item>
                )}
              />

              <Controller
                name="brandId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Brand" className={styles.mappingField}>
                    <Select
                      {...field}
                      allowClear
                      showSearch
                      placeholder="Select brand"
                      options={brandOptions}
                      loading={brandsLoading}
                      optionFilterProp="label"
                      onOpenChange={(visible) => {
                        if (visible) void fetchBrands(DROPDOWN_LIST_PARAMS);
                      }}
                      onChange={(value) => {
                        field.onChange(value);
                        if (value) {
                          setValue('categoryId', undefined);
                          setValue('productId', undefined);
                          setValue('link', '');
                        }
                      }}
                      disabled={isLoading || Boolean(categoryId || productId)}
                    />
                  </Form.Item>
                )}
              />

              <Controller
                name="productId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Product" className={styles.mappingField}>
                    <Select
                      {...field}
                      allowClear
                      showSearch
                      placeholder="Select product"
                      options={productOptions}
                      loading={productsLoading}
                      optionFilterProp="label"
                      onOpenChange={(visible) => {
                        if (visible) void fetchProducts(DROPDOWN_LIST_PARAMS);
                      }}
                      onChange={(value) => {
                        field.onChange(value);
                        if (value) {
                          setValue('categoryId', undefined);
                          setValue('brandId', undefined);
                          setValue('link', '');
                        }
                      }}
                      disabled={isLoading || Boolean(categoryId || brandId)}
                    />
                  </Form.Item>
                )}
              />
            </div>

            {!hasMapping ? (
              <Controller
                name="link"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Custom link">
                    <Input {...field} placeholder="/collections/new-arrival" />
                  </Form.Item>
                )}
              />
            ) : (
              <Form.Item label="Generated link">
                <Input value="Link will be generated automatically on save" disabled />
              </Form.Item>
            )}

            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Form.Item label="Status" className={styles.statusField}>
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    style={{ background: field.value ? BRAND.primary : undefined }}
                  />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <div className={styles.actions}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEdit ? 'Save changes' : 'Create banner'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
